(() => {
  // Loaded by the sections that render these cards (cart-samples-gifts-tabs, cart-drawer), which can
  // appear together, and by the cart drawer once it has cart markup, long after DOMContentLoaded. The cards
  // are re-rendered with every cart change, so they are set up by hbInitSuggestedItems(root), which skips
  // the ones it has already seen, and the delegated click handler is bound once.
  if (window.hbInitSuggestedItems) return;

  const isLoyaltyGate = (variant) => {
    const value = variant?.metafields?.custom?.loyaltyGate ?? variant?.loyaltyGate;
    return value === true || value === "true";
  };
  const updateLoyaltyLoginDisplay = (card, variant) => {
    if (!card || !variant) return;
    const shouldGate = isLoyaltyGate(variant);
    card.querySelectorAll(".buy-buttons-container.login-to-shop, .buy-buttons-container.coming-soon").forEach((el) => {
      el.classList.toggle("hide", !shouldGate);
      if (shouldGate) el.setAttribute("data-loyalty-login", "true");
      else el.removeAttribute("data-loyalty-login");
    });
    card.querySelectorAll(".buy-buttons-container:not(.login-to-shop):not(.coming-soon)").forEach((el) => {
      el.classList.toggle("hide", shouldGate);
    });
    if (shouldGate) {
      const waitlist = card.querySelector(".waitlist-container");
      const oos = card.querySelector(".back-in-stock-container");
      if (waitlist) waitlist.style.display = "none";
      if (oos) oos.style.display = "none";
    }
  };
  const isLoyaltyLogin = (root) => Boolean(root?.querySelector?.("[data-loyalty-login='true']"));
  const isGiftLocked = (card) => card?.dataset.giftLocked === "true" || card?.dataset.thresholdLocked === "true";
  const isOutOfStockGift = (card, variant) =>
    card?.classList.contains("suggested-items__product-card__wrapper--gift-picker") && variant?.available === false;
  const hideAuxiliaryCtasForGiftLock = (card) => {
    const waitlistContainer = card?.querySelector(".waitlist-container");
    const outOfStockContainer = card?.querySelector(".back-in-stock-container");
    const productFormButtons = card?.querySelector(".product-form__buttons");

    if (waitlistContainer) {
      waitlistContainer.style.display = "none";
    }
    if (outOfStockContainer) {
      outOfStockContainer.innerHTML = "";
      outOfStockContainer.style.display = "none";
    }
    if (productFormButtons) productFormButtons.classList.remove("hide");
  };
  const resolveWaitlistEnabled = (variant) => {
    if (!variant) return false;
    const effective = variant.effectiveWaitlist;
    const fallback = variant.enableWaitlist;
    const value = effective !== undefined && effective !== null ? effective : fallback;
    return value === true || value === "true";
  };
  const enforceGiftLock = (card) => {
    if (!card || !isGiftLocked(card)) return;
    if (card.querySelector(".back-in-stock-container .out-of-stock-btn")) return;

    hideAuxiliaryCtasForGiftLock(card);

    card.querySelectorAll(".product-form__submit").forEach((button) => {
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.dataset.giftLock = "true";
      button.dataset.thresholdLock = "true";
    });

    const variantInput = card.querySelector(".product-variant-id");
    if (variantInput) {
      variantInput.disabled = true;
    }
  };

  const updateProductPrice = (card, price, compareAtPrice, savePrice) => {
    const container = card.querySelector(".price-section-data");
    const priceSale = card.querySelector(".price__sale");
    const priceRegular = card.querySelector(".price__regular");
    const comparePriceEl = card.querySelector(".product-compare-at-price");
    const savePriceEl = card.querySelector(".product-save-price");
    // The variant JSON stays Latin; with Arabic numbers (theme setting) it is converted where it is shown.
    const shownMoney = (value) => (window.hbNumerals ? window.hbNumerals.moneyString(value) : value);

    card.querySelectorAll(".product-price").forEach((el) => {
      el.innerHTML = shownMoney(price);
    });

    const isOnSale = compareAtPrice && compareAtPrice !== price && compareAtPrice !== "$0.00";

    container.classList.toggle("price--on-sale", isOnSale);
    priceSale.style.display = isOnSale ? "flex" : "none";
    priceRegular.style.display = isOnSale ? "none" : "flex";

    if (isOnSale) {
      comparePriceEl.innerHTML = shownMoney(compareAtPrice);
      savePriceEl.innerHTML = shownMoney(savePrice);
    }
  };

  const updateUndertoneValue = (card, undertone, labelText, isNew) => {
    const formatted = undertone
      ? window.hbToneLabel
        ? window.hbToneLabel(undertone)
        : undertone.charAt(0).toUpperCase() + undertone.slice(1)
      : "";

    card.querySelectorAll(".custom-select").forEach((select) => {
      // Arabic numbers (theme setting): a size label ("30ml") reads Arabic; shade names come back unchanged.
      select.querySelector(".option-value").textContent = window.hbNumerals ? window.hbNumerals.size(labelText) : labelText;

      const undertoneEl = select.querySelector(".variant-undertone");
      if (undertoneEl) {
        undertoneEl.textContent = formatted ? window.variantStrings.undertoneLabel.replace("[value]", formatted) : "";
      }

      const selectedNewBadge = select.querySelector(".selected-new-badge");
      if (selectedNewBadge && typeof isNew === "boolean") {
        selectedNewBadge.style.display = isNew ? "" : "none";
      }
    });
  };

  const updateShortDescription = (card, description, size) => {
    const shortDescriptionEl = card.querySelector(".featured-card_product-description");
    if (!shortDescriptionEl) return;

    const contentMode = shortDescriptionEl.dataset.contentMode || "description";
    if (contentMode === "hidden") {
      shortDescriptionEl.classList.add("hide");
      return;
    }

    if (contentMode === "static-label") {
      shortDescriptionEl.classList.toggle("hide", !shortDescriptionEl.textContent.trim());
      return;
    }

    if (description || size) {
      let html = "";
      if (description) {
        html += `<span class="inner-description">${description}</span>`;
      }
      if (size) {
        html += ` <span class="hide-on-mobile">●</span>`;
        html += ` <span>${window.hbNumerals ? window.hbNumerals.size(size) : size}</span>`;
      }
      shortDescriptionEl.innerHTML = html;
      shortDescriptionEl.classList.remove("hide");
    } else {
      shortDescriptionEl.classList.add("hide");
    }
  };

  const updateSuggestedItemOutOfStockDisplay = (card, variant, parsedData) => {
    const outOfStockContainer = card.querySelector(".back-in-stock-container");
    const productFormButtons = card.querySelector(".product-form__buttons");
    const waitlistContainer = card.querySelector(".waitlist-container");

    if (isGiftLocked(card) && !isOutOfStockGift(card, variant)) {
      hideAuxiliaryCtasForGiftLock(card);
      return;
    }

    if (!variant) {
      if (outOfStockContainer) outOfStockContainer.style.display = "none";
      if (productFormButtons) productFormButtons.classList.remove("hide");
      return;
    }
    if (isLoyaltyLogin(card)) {
      return;
    }

    const variantOnlineValue = variant.variantOnline;
    const backInStockValue = variant.backInStock;
    const isWaitlistEnabled = resolveWaitlistEnabled(variant);

    // Reset states initially to ensure proper visibility changes
    if (outOfStockContainer) outOfStockContainer.innerHTML = ""; // Clear existing messages
    if (outOfStockContainer) outOfStockContainer.style.display = "none";
    if (productFormButtons) productFormButtons.classList.remove("hide");

    if (isOutOfStockGift(card, variant)) {
      if (waitlistContainer) waitlistContainer.style.display = "none";
      if (outOfStockContainer) {
        const productHandle = parsedData.handle || card.dataset.handle || variant.handle;
        outOfStockContainer.innerHTML = `
          <button
            type="button"
            class="button button--full-width out-of-stock-btn"
            disabled
            aria-disabled="true">
            ${window.getStockStatusLabel?.(productHandle) || window.variantStrings?.soldOut}
          </button>
        `;
        outOfStockContainer.style.display = "block";
      }
      if (productFormButtons) productFormButtons.classList.add("hide");
      return;
    }

    if (variantOnlineValue === "false") {
      if (outOfStockContainer) {
        outOfStockContainer.innerHTML = `
          <button type="button" class="back-in-stock-btn" disabled>
            ${window.variantStrings.unavailable}
          </button>
        `;
        outOfStockContainer.style.display = "block";
      }
      if (productFormButtons) productFormButtons.classList.add("hide");
      return;
    }

    // Check if waitlist should be shown first (takes priority over out-of-stock)
    const shouldShowWaitlist =
      isWaitlistEnabled && (variant.available || (!variant.available && backInStockValue === "true"));

    // If waitlist should be shown, don't show out-of-stock
    if (shouldShowWaitlist) {
      outOfStockContainer.innerHTML = "";
    }

    const shouldShowOutOfStock = !variant.available && backInStockValue === "true" && !isWaitlistEnabled;
    const shouldShowBackInStock = !variant.available && backInStockValue !== "true";
    const productHandle = parsedData.handle || card.dataset.handle || variant.handle;

    if (shouldShowOutOfStock) {
      if (outOfStockContainer) {
        outOfStockContainer.innerHTML = `
          <button
            type="button"
            class="button button--full-width out-of-stock-btn" disabled>
            ${window.getStockStatusLabel?.(productHandle) || window.variantStrings?.soldOut}
          </button>
        `;
        outOfStockContainer.style.display = "block";
      }
      if (productFormButtons) productFormButtons.classList.add("hide");
    } else if (shouldShowBackInStock) {
      if (outOfStockContainer) {
        const root = window.Shopify?.routes?.root || "/";
        const productUrl = `${root}products/${productHandle || ""}`;
        const productTitle =
          card.querySelector("[dl-product-name]")?.getAttribute("dl-product-name") ||
          parsedData?.product?.title ||
          parsedData?.title ||
          "";
        const notifyLabel = (
          window.variantStrings?.notifyMeForProduct || "Notify me about [product_name]"
        ).replace("[product_name]", productTitle);
        const escapedNotifyLabel = notifyLabel
          .replace(/&/g, "&amp;")
          .replace(/"/g, "&quot;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        outOfStockContainer.innerHTML = `
          <div class="back-in-stock-form">
            <a href="${productUrl}" class="button button button--full-width button--quaternary notify-me-btn" aria-label="${escapedNotifyLabel}">${window.variantStrings?.notifyMe || "Notify Me"}</a>
          </div>
        `;
        outOfStockContainer.style.display = "block";
      }
      if (productFormButtons) productFormButtons.classList.add("hide");
    } else {
      if (productFormButtons) productFormButtons.classList.remove("hide");
      if (outOfStockContainer) outOfStockContainer.innerHTML = "";
      if (outOfStockContainer) outOfStockContainer.style.display = "none";
    }

    // If waitlist should be shown, make sure buy buttons are hidden
    if (shouldShowWaitlist) {
      productFormButtons.classList.add("hide");
    }
  };

  const updateSuggestedItemWaitlistDisplay = (card, variant) => {
    const productFormButtons = card.querySelector(".product-form__buttons");
    const waitlistContainer = card.querySelector(".waitlist-container");

    if (isGiftLocked(card)) {
      hideAuxiliaryCtasForGiftLock(card);
      return;
    }

    if (!variant || isLoyaltyLogin(card)) {
      return;
    }

    const isWaitlistEnabled = resolveWaitlistEnabled(variant);
    const backInStockValue = variant.backInStock;

    // New waitlist logic: 2 cases
    // 1. Variant available AND waitlist = true
    // 2. Not available AND waitlist = true AND back_in_stock_disabled = true
    const shouldShowWaitlist =
      isWaitlistEnabled && (variant.available || (!variant.available && backInStockValue === "true"));

    if (shouldShowWaitlist) {
      // Hide buy buttons when waitlist is enabled
      if (productFormButtons) {
        productFormButtons.classList.add("hide");
      }

      // Show waitlist form
      if (waitlistContainer) {
        waitlistContainer.style.display = "block";
      }
    } else {
      // Show buy buttons when waitlist is disabled
      if (productFormButtons) {
        productFormButtons.classList.remove("hide");
      }

      // Hide waitlist form
      if (waitlistContainer) {
        waitlistContainer.style.display = "none";
      }
    }
  };

  const resolveSuggestedItemCard = (option) => {
    const directCard = option.closest(".suggested-items__product-card__wrapper");
    if (directCard) return directCard;

    const anchoredHost = option.closest(".cart-page-picker__anchored-dropdown-host");
    if (!anchoredHost) return null;

    const anchoredSelect =
      anchoredHost.__anchoredSelect ||
      anchoredHost.closest(".cart-page-picker__panel")?.querySelector(".custom-select.open");

    return anchoredSelect?.closest(".suggested-items__product-card__wrapper") || null;
  };

  const handleCustomOptionClick = (event) => {
    const option = event.target.closest(".custom-option");
    if (!option) return;

    const card = resolveSuggestedItemCard(option);
    if (!card) return;

    const productDataJSON = card.querySelector("[suggested-items-data-selected-variant-meta]")?.innerHTML;
    if (!productDataJSON) return;

    const parsedData = JSON.parse(productDataJSON);

    const is_bundled = card.className.includes("bundled-suggested-item");
    let variant;

    if (is_bundled) {
      let selected_option_name;
      if (option.className.includes("mobile-bundle-option")) {
        selected_option_name = Array.from(card.querySelectorAll(".selected-bundle-option.mobile-bundle-option"));
      } else {
        selected_option_name = Array.from(card.querySelectorAll(".selected-bundle-option.desktop-bundle-option"));
      }
      const rawPosition = option.dataset.position;
      const position = parseInt(rawPosition);
      const option_item = option.dataset.value;
      if (!isNaN(position) && selected_option_name[position]) {
        selected_option_name[position].value = option_item;
      }
      const filtered_options = Array.from(selected_option_name)
        .filter((option) => !option.classList.contains("single-product"))
        .map((option) => option.value.trim());

      variant = parsedData.variants.find((inner_variant) => {
        const inner_options = [...inner_variant.options].map((opt) => opt.trim());
        return (
          inner_options.length === filtered_options.length &&
          inner_options.every((option, index) => option === filtered_options[index])
        );
      });
    } else {
      variant = parsedData.variants?.find((item) => item.id == option.dataset.id);
      if (!variant) return;
    }

    const swatchIdInput = card.querySelector(".product-variant-id");
    if (swatchIdInput) {
      swatchIdInput.value = variant.id;
      swatchIdInput.disabled = !variant.available;
    }

    // While a cart write holds the global add-to-cart lock, the product form applies the shade's availability
    // when the lock lifts (product-form.js setVariantAvailable), so a pick cannot enable Add mid-write.
    const submitButton = card.querySelector("product-form .product-form__submit");
    const productForm = submitButton?.closest("product-form");
    if (typeof productForm?.setVariantAvailable === "function" && productForm.submitButton === submitButton) {
      productForm.setVariantAvailable(variant.available);
    } else if (submitButton) {
      submitButton.disabled = !variant.available;
    }

    const inventoryInput = card.querySelector('input[name="inventory-quantity"]');
    const maxQuantityInput = card.querySelector('input[name="max-quantity"]');
    if (inventoryInput) inventoryInput.value = variant.inventoryQuantity;
    if (maxQuantityInput) maxQuantityInput.value = variant.maxQuantity;

    if (!is_bundled) {
      const image = card.querySelector(".suggested-items-image");
      if (image && variant.image_url?.[0]) {
        image.src = variant.image_url[0];
      }
    }

    updateShortDescription(card, variant.shortDescription, variant.size);
    if (!is_bundled) {
      const labelText = option.dataset.value || option.getAttribute("aria-label") || "";
      updateUndertoneValue(card, variant.underTone, labelText, option.dataset.isNew === "true");
    }
    updateProductPrice(card, variant.price, variant.compare_at_price, variant.save_price);
    updateLoyaltyLoginDisplay(card, variant);
    updateSuggestedItemWaitlistDisplay(card, variant);
    updateSuggestedItemOutOfStockDisplay(card, variant, parsedData);
    enforceGiftLock(card);
  };

  const initSuggestedItemCard = (card) => {
    card.dataset.hbSuggestedInit = "true";

    const productDataJSON = card.querySelector("[suggested-items-data-selected-variant-meta]")?.innerHTML;
    if (productDataJSON) {
      try {
        const parsedData = JSON.parse(productDataJSON);
        const default_selected_variant = parsedData.default_selected_variant;
        if (default_selected_variant) {
          updateSuggestedItemWaitlistDisplay(card, default_selected_variant);
          updateSuggestedItemOutOfStockDisplay(card, default_selected_variant, parsedData);
        }
      } catch (error) {
        console.error("Error reading suggested item data:", error);
      }
    }
    enforceGiftLock(card);
  };

  // Sets up the cards under root (the whole document by default) that have not been set up yet.
  window.hbInitSuggestedItems = (root = document) => {
    if (!window.__hbSuggestedItemsInit) {
      window.__hbSuggestedItemsInit = true;
      document.body.addEventListener("click", handleCustomOptionClick);
    }

    const cards = Array.from(root.querySelectorAll(".suggested-items__product-card__wrapper"));
    if (root.matches?.(".suggested-items__product-card__wrapper")) cards.unshift(root);
    cards.forEach((card) => {
      if (!card.hasAttribute("data-hb-suggested-init")) initSuggestedItemCard(card);
    });
  };

  document.addEventListener("hb:cart:rendered", () => window.hbInitSuggestedItems());

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => window.hbInitSuggestedItems());
  } else {
    window.hbInitSuggestedItems();
  }
})();
